import { Contact, ContactListItem } from '@rsmaids/shared/types/contacts';

export interface CachedContactResult {
  data: Contact | null;
  fromCache: boolean;
}

export interface CachedContactListResult {
  data: ContactListItem[];
  fromCache: boolean;
  stale?: boolean;
}

/**
 * Get contact data with localStorage caching
 * @param contactId - The contact ID to fetch
 * @param fetchFunction - Function to fetch contact from API if cache miss
 * @param cacheExpiryMinutes - Cache expiration time in minutes (default: 5)
 * @returns Promise with contact data and cache status
 */
export async function getCachedContact(
  contactId: string,
  fetchFunction: () => Promise<Contact | null>,
  cacheExpiryMinutes: number = 5
): Promise<CachedContactResult> {
  const cacheKey = `contact-${contactId}`;
  const cached = localStorage.getItem(cacheKey);
  const cacheTime = localStorage.getItem(`${cacheKey}-time`);
  const isCacheValid = cached && cacheTime &&
    (Date.now() - parseInt(cacheTime)) < cacheExpiryMinutes * 60 * 1000;

  if (isCacheValid) {
    try {
      const data = JSON.parse(cached);
      console.log(`Using cached contact data for ${contactId}`);
      return { data, fromCache: true };
    } catch {
      // Cache corrupted, continue to API call
    }
  }

  // Cache miss or corrupted - fetch from API
  const data = await fetchFunction();

  // Update cache with fresh data
  if (data) {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(`${cacheKey}-time`, Date.now().toString());
  }

  return { data, fromCache: false };
}

/**
 * Get contact list data with localStorage caching
 * @param fetchFunction - Function to fetch contact list from API if cache miss
 * @param cacheExpiryMinutes - Cache expiration time in minutes (default: 30)
 * @returns Promise with contact list data and cache status
 */
export async function getCachedContactList(
  fetchFunction: () => Promise<ContactListItem[]>,
  cacheExpiryMinutes: number = 30
): Promise<CachedContactListResult> {
  const t0 = performance.now();
  const cacheKey = 'cachedContacts';
  const timeKey = 'contactsLastLoadTime';
  const cached = localStorage.getItem(cacheKey);
  const cacheTime = localStorage.getItem(timeKey);
  const age = cacheTime ? Math.round((Date.now() - parseInt(cacheTime)) / 1000) : null;
  console.log(`[cache] cached=${!!cached} cacheTime=${age}s ago — ${(performance.now()-t0).toFixed(0)}ms`);

  const isCacheValid = cached && cacheTime &&
    (Date.now() - parseInt(cacheTime)) < cacheExpiryMinutes * 60 * 1000;

  if (isCacheValid) {
    try {
      const data = JSON.parse(cached);
      console.log(`[cache] FRESH — ${data.length} contacts from localStorage in ${(performance.now()-t0).toFixed(0)}ms`);
      return { data, fromCache: true };
    } catch {
      console.warn('[cache] localStorage parse failed (fresh path)');
    }
  }

  if (cached) {
    try {
      const data = JSON.parse(cached);
      console.log(`[cache] STALE — ${data.length} contacts from localStorage in ${(performance.now()-t0).toFixed(0)}ms`);
      return { data, fromCache: true, stale: true };
    } catch {
      console.warn('[cache] localStorage parse failed (stale path)');
    }
  }

  console.log(`[cache] NO CACHE — calling fetchFunction at ${(performance.now()-t0).toFixed(0)}ms`);
  const t1 = performance.now();
  const data = await fetchFunction();
  console.log(`[cache] fetchFunction returned ${data.length} contacts in ${(performance.now()-t1).toFixed(0)}ms`);

  localStorage.setItem(cacheKey, JSON.stringify(data));
  localStorage.setItem(timeKey, Date.now().toString());

  return { data, fromCache: false };
}

/**
 * A contact's primary phone from the cached contact list — read straight from localStorage, no network, so
 * the thread can start loading the contact's texts at once. Undefined if the list isn't cached or has no number.
 */
export function getCachedContactPhone(contactId: string): string | undefined {
  try {
    const cached = localStorage.getItem('cachedContacts');
    if (!cached) return undefined;
    return (JSON.parse(cached) as ContactListItem[]).find(c => c.id === contactId)?.primaryPhone || undefined;
  } catch {
    return undefined;
  }
}
